/**
 * Integrations Extras API module - integration-adjacent surface that does
 * not fit into integrations.ts (which owns ServiceNow/Slack/Jira/ticket-sync/
 * integration-hub). This module hosts data-lake exports, second-generation
 * event-stream exports, third-party finding ingestion, telemetry gaps,
 * detection-rule library, and dashboards/boards.
 *
 * Real per-domain split: contains the actual implementations, extracted
 * verbatim from the monolithic frontend/src/services/api.ts as part of the
 * ADR-0003 follow-up.
 *
 * Domain objects:
 *   - dataLakeApi              (data-lake export configs + history/stats)
 *   - eventStreamApi2          (second-gen event stream configs + publish/test/stats)
 *   - thirdPartyIngestionApi   (third-party finding sources + ingest/jobs/findings)
 *   - telemetryGapApi          (CDR telemetry gaps + coverage + recommendations)
 *   - detectionRuleLibraryApi  (detection rules library + CRUD/toggle/test/stats)
 *   - boardsApi                (dashboards/boards - list/get/create/delete/recent)
 */
import { _thenUnwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';

// -- dataLakeApi --
export const dataLakeApi = {
  createConfig: (data: any) => fetchClient.post('/api/v1/exports/data-lake/configs', data).then(_thenUnwrap),
  listConfigs: () => fetchClient.get('/api/v1/exports/data-lake/configs').then(_thenUnwrap),
  updateConfig: (id: string, data: any) => fetchClient.put(`/api/v1/exports/data-lake/configs/${id}`, data).then(_thenUnwrap),
  deleteConfig: (id: string) => fetchClient.delete(`/api/v1/exports/data-lake/configs/${id}`).then(_thenUnwrap),
  triggerExport: (id: string) => fetchClient.post(`/api/v1/exports/data-lake/trigger/${id}`).then(_thenUnwrap),
  getHistory: () => fetchClient.get('/api/v1/exports/data-lake/history').then(_thenUnwrap),
  getStats: () => fetchClient.get('/api/v1/exports/data-lake/stats').then(_thenUnwrap),
};

// -- eventStreamApi2 --
export const eventStreamApi2 = {
  createConfig: (data: any) => fetchClient.post('/api/v1/exports/event-stream/configs', data).then(_thenUnwrap),
  listConfigs: () => fetchClient.get('/api/v1/exports/event-stream/configs').then(_thenUnwrap),
  updateConfig: (id: string, data: any) => fetchClient.put(`/api/v1/exports/event-stream/configs/${id}`, data).then(_thenUnwrap),
  deleteConfig: (id: string) => fetchClient.delete(`/api/v1/exports/event-stream/configs/${id}`).then(_thenUnwrap),
  testConfig: (id: string) => fetchClient.post(`/api/v1/exports/event-stream/test/${id}`).then(_thenUnwrap),
  publish: (event: any) => fetchClient.post('/api/v1/exports/event-stream/publish', event).then(_thenUnwrap),
  getStats: () => fetchClient.get('/api/v1/exports/event-stream/stats').then(_thenUnwrap),
};

// -- thirdPartyIngestionApi --
export const thirdPartyIngestionApi = {
  // Sources
  getSources: (params?: { type?: string; status?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/graph/ingestion/sources', params).then(_thenUnwrap),
  createSource: (data: { name: string; type: string; config: Record<string, unknown>; enabled?: boolean }) =>
    fetchClient.post('/api/v1/graph/ingestion/sources', data).then(_thenUnwrap),
  updateSource: (sourceId: string, data: { name?: string; config?: Record<string, unknown>; enabled?: boolean }) =>
    fetchClient.put(`/api/v1/graph/ingestion/sources/${sourceId}`, data).then(_thenUnwrap),
  deleteSource: (sourceId: string) =>
    fetchClient.delete(`/api/v1/graph/ingestion/sources/${sourceId}`).then(_thenUnwrap),
  testSource: (sourceId: string) =>
    fetchClient.post(`/api/v1/graph/ingestion/sources/${sourceId}/test`).then(_thenUnwrap),

  // Ingestion
  ingest: (data: { source_id: string; findings: Record<string, unknown>[] }) =>
    fetchClient.post('/api/v1/graph/ingestion/ingest', data).then(_thenUnwrap),
  // R152: the third argument to apiClient.post is the QUERY PARAMS map, not an
  // axios-style config, so this appended a literal `?headers=[object Object]`
  // and the header was never applied. That was lucky rather than harmless: the
  // client already detects FormData and sends it raw precisely so the BROWSER
  // can set `multipart/form-data; boundary=...`, and a hand-set Content-Type
  // with no boundary would have made the upload unparseable server-side.
  ingestCsv: (sourceId: string, formData: FormData) =>
    fetchClient.post('/api/v1/graph/ingestion/ingest/csv', formData).then(_thenUnwrap),

  // Jobs
  getJobs: (params?: { source_id?: string; status?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/graph/ingestion/jobs', params).then(_thenUnwrap),
  getJobDetail: (jobId: string) =>
    fetchClient.get(`/api/v1/graph/ingestion/jobs/${jobId}`).then(_thenUnwrap),

  // Findings
  getFindings: (params?: { source_id?: string; severity?: string; status?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/graph/ingestion/findings', params).then(_thenUnwrap),
  getFindingDetail: (findingId: string) =>
    fetchClient.get(`/api/v1/graph/ingestion/findings/${findingId}`).then(_thenUnwrap),

  // Stats & Enrichment
  getStats: () =>
    fetchClient.get('/api/v1/graph/ingestion/stats').then(_thenUnwrap),
  triggerEnrich: (data: { finding_ids: string[] }) =>
    fetchClient.post('/api/v1/graph/ingestion/enrich', data).then(_thenUnwrap),
};

// -- telemetryGapApi --
export const telemetryGapApi = {
  getGaps: (params?: { provider?: string; severity?: string; status?: string; page?: number; page_size?: number }) =>
    fetchClient.get('/api/v1/cdr/telemetry/gaps', params).then(_thenUnwrap),
  getCoverage: (params?: { provider?: string; resource_type?: string }) =>
    fetchClient.get('/api/v1/cdr/telemetry/coverage', params).then(_thenUnwrap),
  getProviderCoverage: (provider: string) =>
    fetchClient.get(`/api/v1/cdr/telemetry/coverage/${provider}`).then(_thenUnwrap),
  getRecommendations: (params?: { provider?: string; gap_id?: string }) =>
    fetchClient.get('/api/v1/cdr/telemetry/recommendations', params).then(_thenUnwrap),
  assess: (data: { provider: string; account_id: string; regions?: string[] }) =>
    fetchClient.post('/api/v1/cdr/telemetry/assess', data).then(_thenUnwrap),
  getStats: () =>
    fetchClient.get('/api/v1/cdr/telemetry/stats').then(_thenUnwrap),
};

// -- detectionRuleLibraryApi --
export const detectionRuleLibraryApi = {
  getRules: (params?: any) => fetchClient.get('/api/v1/detection-rules/library', params).then(_thenUnwrap),
  getStats: () => fetchClient.get('/api/v1/detection-rules/library/stats').then(_thenUnwrap),
  getMitreCoverage: () => fetchClient.get('/api/v1/detection-rules/library/mitre-coverage').then(_thenUnwrap),
  // Wiz-parity CRUD + toggle + test for the detection rules library.
  // Custom rules land in csp_cdr.detection_rules; built-in toggles land
  // in csp_cdr.detection_rule_overrides.
  toggle: (ruleId: string, enabled: boolean) =>
    fetchClient.patch(`/api/v1/detection-rules/${encodeURIComponent(ruleId)}/enabled`, { enabled, by: 'ui' }).then(_thenUnwrap),
  create: (data: any) =>
    fetchClient.post('/api/v1/detection-rules', data).then(_thenUnwrap),
  update: (ruleId: string, data: any) =>
    fetchClient.patch(`/api/v1/detection-rules/${encodeURIComponent(ruleId)}`, data).then(_thenUnwrap),
  remove: (ruleId: string) =>
    fetchClient.delete(`/api/v1/detection-rules/${encodeURIComponent(ruleId)}`).then(_thenUnwrap),
  test: (ruleId: string, hours: number = 24) =>
    fetchClient.post(`/api/v1/detection-rules/${encodeURIComponent(ruleId)}/test`, { hours }).then(_thenUnwrap),
  // Per-rule scan metrics (csp_cspm.rule_execution_stats). Shape mirrors
  // the graph-engine rule-stats endpoint so UI code can be shared.
  // R152: `{ params: { days } }` sent `?params=[object Object]`, so the caller's
  // window was dropped and every rule-stats panel silently rendered the
  // server's default window regardless of the selected range.
  stats: (ruleId: string, days: number = 30) =>
    fetchClient.get(`/api/v1/cspm/rules/${encodeURIComponent(ruleId)}/stats`, { days }).then(_thenUnwrap),
};

// -- boardsApi --
export const boardsApi = {
  list: () => fetchClient.get('/api/v1/boards').then(_thenUnwrap),
  get: (boardId: string) => fetchClient.get(`/api/v1/boards/${boardId}`).then(_thenUnwrap),
  create: (data: any) => fetchClient.post('/api/v1/boards', data).then(_thenUnwrap),
  delete: (boardId: string) => fetchClient.delete(`/api/v1/boards/${boardId}`).then(_thenUnwrap),
  getRecent: () => fetchClient.get('/api/v1/boards/recent/list').then(_thenUnwrap),
};
